import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { formatarHoras } from '../utils/formatarHoras';

const VisualizacaoHorarioSimples = ({ 
  horario, 
  colaboradores, 
  colaboradorSelecionadoId = null, 
  modoIndividual = false,
  modoColaborador = false,
  visualizarTodos = false
}) => {
  const [colaboradorSelecionado, setColaboradorSelecionado] = useState(null);
  const [semanaSelecionada, setSemanaSelecionada] = useState(0);

  // Se foi passado um ID de colaborador, selecionar automaticamente
  useEffect(() => {
    if (colaboradorSelecionadoId && horario && horario.colaboradores_horarios) {
      const colab = horario.colaboradores_horarios.find(c => c.colaborador_id === colaboradorSelecionadoId);
      if (colab) {
        setColaboradorSelecionado(colab);
      }
    } else {
      // Se não há colaborador selecionado, limpar seleção
      setColaboradorSelecionado(null);
    }
  }, [colaboradorSelecionadoId, horario]);

  if (!horario || !horario.colaboradores_horarios) {
    return (
      <div className="text-center p-8 text-gray-500">
        Nenhum horário disponível para visualização
      </div>
    );
  }

  // Modo Colaborador: Visualização horizontal sem resumo geral
  if (modoColaborador) {
    let colaboradoresParaMostrar = [];
    
    if (visualizarTodos) {
      // Mostrar todos os colaboradores
      colaboradoresParaMostrar = horario.colaboradores_horarios || [];
    } else if (colaboradorSelecionado) {
      // Mostrar apenas o colaborador selecionado
      colaboradoresParaMostrar = [colaboradorSelecionado];
    } else if (colaboradorSelecionadoId && horario.colaboradores_horarios) {
      // Tentar encontrar o colaborador pelo ID
      const colab = horario.colaboradores_horarios.find(c => c.colaborador_id === colaboradorSelecionadoId);
      if (colab) {
        colaboradoresParaMostrar = [colab];
      }
    }

    if (colaboradoresParaMostrar.length === 0) {
      return (
        <div className="text-center p-8 text-gray-500">
          Nenhum horário disponível para visualização
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {colaboradoresParaMostrar.map((colab) => {
          const registosPorDia = colab.registos_por_dia || {};
          const todasDatas = Object.keys(registosPorDia).sort();
          
          // Dividir em semanas
          const semanas = [];
          for (let i = 0; i < todasDatas.length; i += 7) {
            semanas.push(todasDatas.slice(i, i + 7));
          }

          return (
            <Card key={colab.colaborador_id} className="mb-4">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100">
                <CardTitle className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center font-bold">
                    {colab.colaborador_nome.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-lg">{colab.colaborador_nome}</div>
                    <div className="text-sm text-gray-600 font-normal">
                      {new Date(horario.periodo_inicio).toLocaleDateString('pt-PT')} - {new Date(horario.periodo_fim).toLocaleDateString('pt-PT')}
                    </div>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                {/* Mostrar cada semana numa tabela com dias fixos no topo */}
                {semanas.map((semana, semanaIdx) => (
                  <div key={semanaIdx} className="mb-6">
                    <h4 className="font-bold text-gray-700 mb-3 py-2">Semana {semanaIdx + 1}</h4>
                    {/* Container com scroll e altura máxima - estrutura igual ao HorarioVisualizacaoModal */}
                    <div style={{maxHeight: '60vh', overflow: 'auto', WebkitOverflowScrolling: 'touch'}} className="border rounded-lg">
                      <table className="w-full border-collapse" style={{minWidth: 'max-content', borderSpacing: 0}}>
                        {/* Cabeçalho com dias - FIXO NO TOPO usando estilos inline */}
                        <thead style={{position: 'sticky', top: 0, zIndex: 30}}>
                          <tr style={{boxShadow: '0 2px 4px rgba(0,0,0,0.1)'}}>
                            {semana.map((data) => {
                              const dataObj = new Date(data + 'T00:00:00');
                              const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                              const diaSemana = diasSemana[dataObj.getDay()];
                              const dia = dataObj.getDate();
                              const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                              const mes = meses[dataObj.getMonth()];
                              const ehFimDeSemana = dataObj.getDay() === 0 || dataObj.getDay() === 6;
                              
                              return (
                                <th 
                                  key={data} 
                                  className="p-2 text-center border-b-2 min-w-[120px]"
                                  style={{backgroundColor: ehFimDeSemana ? '#fde68a' : '#dbeafe'}}
                                >
                                  <div className="font-bold text-sm">{diaSemana}</div>
                                  <div className="text-xs text-gray-600">{dia} {mes}</div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            {semana.map((data) => {
                              const registos = registosPorDia[data] || [];
                              const dataObj = new Date(data + 'T00:00:00');
                              const ehFimDeSemana = dataObj.getDay() === 0 || dataObj.getDay() === 6;
                              const temTrocaTurno = registos.some(reg => reg.e_troca === true);

                              return (
                                <td 
                                  key={data} 
                                  className={`p-2 align-top border min-w-[120px] ${ehFimDeSemana ? 'bg-gray-50' : ''} ${temTrocaTurno ? 'bg-orange-50 ring-2 ring-orange-400' : ''}`}
                                >
                                  {temTrocaTurno && (
                                    <div className="mb-2">
                                      <span className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full">
                                        🔄 TROCA
                                      </span>
                                    </div>
                                  )}
                                  <div className="space-y-2">
                                  {registos.length === 0 ? (
                                    <div className="text-center text-gray-400 py-2 text-sm">
                                      Sem turnos
                                    </div>
                                  ) : (
                                    registos.map((reg, idx) => {
                                      const tipo = (reg.tipo || 'normal').toLowerCase();
                                      const local = (reg.local || '').toLowerCase();
                                      
                                      // Detectar reunião: pelo tipo OU pelo local OU por turnos com 0 horas sem entrada/saída real
                                      const ehReuniao = tipo === 'reuniao' || 
                                                       local.includes('reuni') || 
                                                       (reg.horas === 0 && (!reg.entrada || !reg.saida || reg.entrada === '--:--' || reg.saida === '--:--'));
                                      const tipoFinal = ehReuniao ? 'reuniao' : tipo;
                                      
                                      const cores = {
                                        'folga': 'bg-yellow-100 border-yellow-300 text-yellow-800',
                                        'ferias': 'bg-green-100 border-green-300 text-green-800',
                                        'falta': 'bg-red-100 border-red-300 text-red-800',
                                        'normal': 'bg-blue-100 border-blue-300 text-blue-800',
                                        'gestao': 'bg-orange-100 border-orange-300 text-orange-800',
                                        'reuniao': 'bg-purple-100 border-purple-300 text-purple-800',
                                        'baixa': 'bg-pink-100 border-pink-300 text-pink-800',
                                        'apoio': 'bg-teal-100 border-teal-300 text-teal-800'
                                      };
                                      const corClasse = cores[tipoFinal] || 'bg-gray-100 border-gray-300';

                                      // Tipos especiais que mostram só o nome (sem horário)
                                      // NOTA: reunião NÃO está aqui — se tiver entrada/saída válidas, mostra as horas.
                                      if (tipoFinal === 'folga' || tipoFinal === 'ferias' || tipoFinal === 'falta' || tipoFinal === 'baixa') {
                                        return (
                                          <div 
                                            key={idx}
                                            className={`p-2 rounded-lg border-2 text-center font-semibold text-sm ${corClasse}`}
                                          >
                                            {tipoFinal === 'ferias' ? 'FÉRIAS' : tipoFinal.toUpperCase()}
                                          </div>
                                        );
                                      }

                                      // Reunião sem horas definidas → mostra apenas o label
                                      const temHorario = reg.entrada && reg.saida && reg.entrada !== '--:--' && reg.saida !== '--:--';
                                      if (tipoFinal === 'reuniao' && !temHorario) {
                                        return (
                                          <div 
                                            key={idx}
                                            className={`p-2 rounded-lg border-2 text-center font-semibold text-sm ${corClasse}`}
                                            data-testid="reuniao-sem-horas"
                                          >
                                            👥 REUNIÃO
                                          </div>
                                        );
                                      }

                                      return (
                                        <div 
                                          key={idx}
                                          className={`p-2 rounded-lg border ${corClasse} space-y-1 text-xs relative`}
                                        >
                                          {/* Badge de TROCA */}
                                          {reg.e_troca && (
                                            <div className="absolute -top-1 -right-1 z-10">
                                              <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-md flex items-center gap-0.5">
                                                <span>🔄</span>
                                                <span>TROCA</span>
                                              </div>
                                            </div>
                                          )}
                                          
                                          <div className="flex items-center justify-between">
                                            <span className="font-semibold text-xs">
                                              {tipoFinal === 'reuniao' ? '👥 REUNIÃO' : (reg.local || 'Sem local')}
                                            </span>
                                            <span className="bg-white px-1.5 py-0.5 rounded text-[10px]">
                                              {formatarHoras(reg.horas || 0)}
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-1 text-[10px]">
                                            <span>{reg.entrada || '--:--'}</span>
                                            <span>→</span>
                                            <span>{reg.saida || '--:--'}</span>
                                          </div>
                                          {reg.observacao && (
                                            <div className="text-[10px] text-gray-600 italic">
                                              {reg.observacao}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })
                                  )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  // Calcular estatísticas do colaborador
  const calcularEstatisticas = (colab) => {
    const registos = colab.registos_por_dia || {};
    let totalHoras = 0;
    let totalFolgas = 0;
    let totalFerias = 0;
    let totalFaltas = 0;
    let totalTurnos = 0;

    Object.values(registos).forEach(dia => {
      dia.forEach(reg => {
        const tipo = (reg.tipo || 'normal').toLowerCase();
        if (tipo === 'folga') {
          totalFolgas++;
        } else if (tipo === 'ferias') {
          totalFerias++;
        } else if (tipo === 'falta') {
          totalFaltas++;
        } else {
          totalHoras += reg.horas || 0;
          totalTurnos++;
        }
      });
    });

    return { totalHoras, totalFolgas, totalFerias, totalFaltas, totalTurnos };
  };

  // Se não houver colaborador selecionado, mostrar lista de colaboradores com estatísticas
  if (!colaboradorSelecionado) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold mb-4">Selecione um colaborador para ver o horário detalhado:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {horario.colaboradores_horarios.map(colab => {
            const stats = calcularEstatisticas(colab);
            
            return (
              <Card 
                key={colab.colaborador_id} 
                className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-500"
                onClick={() => setColaboradorSelecionado(colab)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center font-bold text-xl shadow-md">
                      {colab.colaborador_nome.substring(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-lg">{colab.colaborador_nome}</p>
                      <p className="text-xs text-gray-500">Clique para ver detalhes</p>
                    </div>
                  </div>
                  
                  {/* Estatísticas */}
                  <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t">
                    <div className="text-center bg-blue-50 rounded-lg p-2">
                      <div className="text-2xl font-bold text-blue-600">{stats.totalHoras.toFixed(1)}</div>
                      <div className="text-xs text-gray-600">Horas</div>
                    </div>
                    <div className="text-center bg-green-50 rounded-lg p-2">
                      <div className="text-2xl font-bold text-green-600">{stats.totalTurnos}</div>
                      <div className="text-xs text-gray-600">Turnos</div>
                    </div>
                    <div className="text-center bg-yellow-50 rounded-lg p-2">
                      <div className="text-2xl font-bold text-yellow-600">{stats.totalFolgas}</div>
                      <div className="text-xs text-gray-600">Folgas</div>
                    </div>
                    <div className="text-center bg-purple-50 rounded-lg p-2">
                      <div className="text-2xl font-bold text-purple-600">{stats.totalFerias}</div>
                      <div className="text-xs text-gray-600">Férias</div>
                    </div>
                  </div>
                  
                  {stats.totalFaltas > 0 && (
                    <div className="mt-3 text-center bg-red-50 rounded-lg p-2">
                      <span className="text-sm font-semibold text-red-600">
                        ⚠️ {stats.totalFaltas} Falta{stats.totalFaltas > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Resumo geral */}
        <Card className="mt-6 bg-gradient-to-r from-blue-50 to-purple-50">
          <CardContent className="p-6">
            <h4 className="font-bold text-lg mb-3">📊 Resumo Geral do Horário</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {horario.colaboradores_horarios.length}
                </div>
                <div className="text-sm text-gray-600">Colaboradores</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {horario.colaboradores_horarios.reduce((sum, c) => sum + calcularEstatisticas(c).totalHoras, 0).toFixed(0)}
                </div>
                <div className="text-sm text-gray-600">Horas Totais</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {horario.colaboradores_horarios.reduce((sum, c) => sum + calcularEstatisticas(c).totalTurnos, 0)}
                </div>
                <div className="text-sm text-gray-600">Turnos Totais</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600">
                  {new Date(horario.periodo_inicio).toLocaleDateString('pt-PT')} - {new Date(horario.periodo_fim).toLocaleDateString('pt-PT')}
                </div>
                <div className="text-sm text-gray-600">Período</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Preparar dados do colaborador selecionado
  const registosPorDia = colaboradorSelecionado.registos_por_dia || {};
  const todasDatas = Object.keys(registosPorDia).sort();
  
  // Dividir em semanas
  const semanas = [];
  for (let i = 0; i < todasDatas.length; i += 7) {
    semanas.push(todasDatas.slice(i, i + 7));
  }

  const semanaAtual = semanas[semanaSelecionada] || [];

  const coresPorTipo = {
    'folga': 'bg-yellow-100 border-yellow-300 text-yellow-800',
    'ferias': 'bg-green-100 border-green-300 text-green-800',
    'falta': 'bg-red-100 border-red-300 text-red-800',
    'normal': 'bg-blue-100 border-blue-300 text-blue-800',
    'gestao': 'bg-orange-100 border-orange-300 text-orange-800'
  };

  const formatarData = (dataStr) => {
    const data = new Date(dataStr + 'T00:00:00');
    const diasSemana = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    
    return {
      diaSemana: diasSemana[data.getDay()],
      dia: data.getDate(),
      mes: meses[data.getMonth()]
    };
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho com navegação */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline" 
          onClick={() => setColaboradorSelecionado(null)}
        >
          ← Voltar à lista
        </Button>
        
        <div className="text-center">
          <h2 className="text-lg font-semibold">{colaboradorSelecionado.colaborador_nome}</h2>
          <p className="text-gray-500">
            Semana {semanaSelecionada + 1} de {semanas.length}
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSemanaSelecionada(Math.max(0, semanaSelecionada - 1))}
            disabled={semanaSelecionada === 0}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSemanaSelecionada(Math.min(semanas.length - 1, semanaSelecionada + 1))}
            disabled={semanaSelecionada === semanas.length - 1}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Cards por dia */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {semanaAtual.map((data, dataIdx) => {
          const registos = registosPorDia[data] || [];
          const dataFormatada = formatarData(data);
          const ehFimDeSemana = new Date(data + 'T00:00:00').getDay() === 0 || 
                                new Date(data + 'T00:00:00').getDay() === 6;
          
          // Detectar troca de turno (verificar se algum registo tem e_troca=true)
          const temTrocaTurno = registos.some(reg => reg.e_troca === true);

          return (
            <div key={data} className="relative">
              {temTrocaTurno && (
                <div className="absolute -top-2 -left-2 z-10">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-lg flex items-center gap-1 animate-pulse">
                    <span>🔄</span>
                    <span>TROCA</span>
                  </div>
                </div>
              )}
              <Card 
                className={`${ehFimDeSemana ? 'bg-gray-50' : ''} ${temTrocaTurno ? 'ring-2 ring-orange-400' : ''}`}
              >
                <CardHeader className={`pb-3 ${ehFimDeSemana ? 'bg-gray-100' : 'bg-blue-50'} ${temTrocaTurno ? 'bg-gradient-to-r from-orange-50 to-red-50' : ''}`}>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <div>
                      <div className="font-bold">{dataFormatada.diaSemana}</div>
                      <div className="text-xs text-gray-600">{dataFormatada.dia} {dataFormatada.mes}</div>
                    </div>
                  </CardTitle>
                </CardHeader>
              <CardContent className="pt-4 space-y-2">
                {registos.length === 0 ? (
                  <div className="text-center text-gray-400 py-4 text-sm">
                    Sem turnos
                  </div>
                ) : (
                  registos.map((reg, idx) => {
                    const tipo = (reg.tipo || 'normal').toLowerCase();
                    const local = (reg.local || '').toLowerCase();
                    
                    // Detectar reunião: pelo tipo OU pelo local OU por turnos com 0 horas sem entrada/saída real
                    const ehReuniao = tipo === 'reuniao' || 
                                     local.includes('reuni') || 
                                     (reg.horas === 0 && (!reg.entrada || !reg.saida || reg.entrada === '--:--' || reg.saida === '--:--'));
                    const tipoFinal = ehReuniao ? 'reuniao' : tipo;
                    
                    // Cores por tipo incluindo reunião
                    const coresTipo = {
                      ...coresPorTipo,
                      'reuniao': 'bg-purple-100 border-purple-300 text-purple-800'
                    };
                    const corClasse = coresTipo[tipoFinal] || 'bg-gray-100 border-gray-300';

                    if (tipoFinal === 'folga' || tipoFinal === 'ferias' || tipoFinal === 'falta' || tipoFinal === 'baixa') {
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border-2 text-center font-semibold ${corClasse}`}
                        >
                          {tipoFinal === 'ferias' ? 'FÉRIAS' : tipoFinal.toUpperCase()}
                        </div>
                      );
                    }

                    // Reunião sem horas definidas → mostra apenas o label
                    const temHorario = reg.entrada && reg.saida && reg.entrada !== '--:--' && reg.saida !== '--:--';
                    if (tipoFinal === 'reuniao' && !temHorario) {
                      return (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border-2 text-center font-semibold ${corClasse}`}
                          data-testid="reuniao-sem-horas"
                        >
                          👥 REUNIÃO
                        </div>
                      );
                    }

                    return (
                      <div 
                        key={idx}
                        className={`p-3 rounded-lg border ${corClasse} space-y-1 relative`}
                      >
                        {/* Badge de TROCA */}
                        {reg.e_troca && (
                          <div className="absolute -top-1 -right-1 z-10">
                            <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-md flex items-center gap-0.5">
                              <span>🔄</span>
                              <span>TROCA</span>
                            </div>
                          </div>
                        )}
                        
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-sm">
                            {tipoFinal === 'reuniao' ? '👥 REUNIÃO' : (reg.local || 'Sem local')}
                          </span>
                          <span className="text-xs bg-white px-2 py-1 rounded">
                            {formatarHoras(reg.horas || 0)}
                          </span>
                        </div>
                        <div className="text-xs flex items-center gap-2">
                          <span>🕐 {reg.entrada || '--:--'}</span>
                          <span>→</span>
                          <span>🕐 {reg.saida || '--:--'}</span>
                        </div>
                        {reg.observacao && (
                          <div className="text-xs text-gray-600 italic mt-1">
                            {reg.observacao}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
            </div>
          );
        })}
      </div>

      {/* Legenda */}
      <Card className="mt-6">
        <CardContent className="p-4">
          <p className="text-sm font-semibold mb-2">Legenda:</p>
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-100 border border-blue-300"></div>
              <span className="text-xs">Turno Normal</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-orange-100 border border-orange-300"></div>
              <span className="text-xs">Gestão</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300"></div>
              <span className="text-xs">Folga</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-green-100 border border-green-300"></div>
              <span className="text-xs">Férias</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-red-100 border border-red-300"></div>
              <span className="text-xs">Falta</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VisualizacaoHorarioSimples;
